'use client';

import { Clock, CircleNotch } from '@phosphor-icons/react';
import { cn } from '@/shared/lib/cn';

const SIZE_PRESETS = {
  sm: { wrap: 'h-4 w-4', font: 'text-[8px]' },
  md: { wrap: 'h-[18px] w-[18px]', font: 'text-[9px]' },
  lg: { wrap: 'h-6 w-6', font: 'text-[10px]' },
  xl: { wrap: 'h-7 w-7', font: 'text-[11px]' },
} as const;

export type HistoryActivityIconSize = keyof typeof SIZE_PRESETS;

interface HistoryActivityIconProps {
  activeCount: number;
  size?: HistoryActivityIconSize;
  className?: string;
  isActive?: boolean;
}

// Active job count includes albums on purpose. Server-side `active_renders`
// excludes them (separate quota), but the user-facing "in progress" badge
// must reflect everything currently working — render, upscale, and album.
export function HistoryActivityIcon({
  activeCount,
  size = 'sm',
  className,
  isActive = false,
}: HistoryActivityIconProps) {
  const preset = SIZE_PRESETS[size];

  if (activeCount > 0) {
    const display = activeCount > 99 ? '99+' : String(activeCount);
    return (
      <span
        className={cn(
          'relative inline-flex items-center justify-center shrink-0',
          preset.wrap,
          className
        )}
        role="status"
        aria-label={`${activeCount} active jobs`}
      >
        <CircleNotch
          className="absolute inset-0 h-full w-full animate-spin"
          weight="bold"
          aria-hidden
        />
        <span
          className={cn(
            'relative font-bold leading-none tabular-nums',
            preset.font
          )}
        >
          {display}
        </span>
      </span>
    );
  }

  return (
    <Clock
      className={cn(preset.wrap, 'shrink-0', className)}
      weight={isActive ? 'fill' : 'regular'}
      aria-hidden
    />
  );
}
